import type { Dictionary } from './zh';

/** English copy. Typed against the Chinese dictionary so keys cannot drift. */
export const en: Dictionary = {
  common: {
    appName: 'Medaka Games Reward',
    loading: 'Loading',
    save: 'Save',
    saving: 'Saving…',
    saved: 'Saved',
    cancel: 'Cancel',
    confirm: 'Confirm',
    close: 'Close',
    delete: 'Delete',
    edit: 'Edit',
    submit: 'Submit',
    submitting: 'Submitting…',
    search: 'Search',
    searchPlaceholder: 'Search nickname or Player ID',
    filter: 'Filter',
    reset: 'Reset',
    all: 'All',
    more: 'More',
    retry: 'Try again',
    back: 'Back',
    next: 'Next',
    previous: 'Previous',
    page: 'Page {{page}}',
    pageOf: 'Page {{page}} of {{total}}',
    results: '{{count}} results',
    signIn: 'Sign in',
    signUp: 'Sign up',
    signOut: 'Sign out',
    copy: 'Copy',
    copied: 'Copied',
    points: 'Points',
    pointsUnit: 'pts',
    rank: 'Rank',
    unranked: 'Unranked',
    time: 'Time',
    player: 'Player',
    you: 'You',
    comingSoon: 'Coming soon',
    optional: 'Optional',
    required: 'Required',
    language: 'Language',
    theme: 'Theme',
    themeDark: 'Dark',
    themeLight: 'Light',
    themeSystem: 'System',
    justNow: 'just now',
    minutesAgo: '{{count}}m ago',
    hoursAgo: '{{count}}h ago',
    daysAgo: '{{count}}d ago',
    viewAll: 'View all',
    empty: 'Nothing here yet',
  },
  nav: {
    home: 'Home',
    achievements: 'Achievements',
    speedruns: 'Speedruns',
    leaderboard: 'Leaderboard',
    community: 'Community',
    players: 'Players',
    profile: 'Profile',
    points: 'Points',
    notifications: 'Notifications',
    settings: 'Settings',
    myProfile: 'My profile',
    menu: 'Menu',
    skipToContent: 'Skip to content',
    links: 'Links',
  },
  terms: {
    title: 'Terms of Service',
    link: 'Terms',
    column: 'Community rules & terms',
    subtitle: 'Please read these terms before using the site. Staff can edit the Chinese and English copies in the admin console.',
    empty: 'Terms have not been published yet. Check back later, or contact the developer.',
  },
  guidelines: {
    title: 'Community rules',
    link: 'Community rules',
    subtitle: 'Please read these rules before using the community. Staff can edit the Chinese and English copies on the terms page in the admin console.',
    empty: 'Community rules have not been published yet. Check back later, or contact the developer.',
  },
  home: {
    eyebrow: 'Player platform',
    title: 'Every clear, every second, on the record',
    subtitle: 'Achievements, speedruns, points and community — the shared record of Medaka Games players.',
    ctaPrimary: 'Create account',
    ctaSecondary: 'View leaderboard',
    ctaSignedIn: 'Go to my profile',
    statPlayers: 'Registered players',
    statAchievements: 'Achievements unlocked',
    statRuns: 'Verified speedruns',
    statPoints: 'Points awarded',
    topPlayers: 'Points leaders',
    fastestRuns: 'Fastest runs',
    latestPosts: 'From the community',
    featureAchievementsTitle: 'Achievements',
    featureAchievementsBody:
      'Normal and special achievements are tracked separately, and hidden ones stay secret until you earn them.',
    featureSpeedrunTitle: 'Speedruns & review',
    featureSpeedrunBody:
      'Submit by platform, version and category. Only runs approved by a human reach the official board.',
    featurePointsTitle: 'Points ledger',
    featurePointsBody:
      'Every point has a source and a reason, and history never changes because a future rule did.',
    featureCommunityTitle: 'Community',
    featureCommunityBody:
      'Share routes and discoveries, follow the people you learn from, push the record forward together.',
    announcementLabel: 'Announcement',
  },
  auth: {
    loginTitle: 'Welcome back',
    loginSubtitle: 'Sign in to see your achievements, runs and points.',
    registerTitle: 'Create your account',
    registerSubtitle: 'Join Medaka Games Reward and start putting your runs on the record.',
    identifier: 'Username, email or Player ID',
    identifierPlaceholder: 'you@example.com',
    username: 'Username',
    usernameHint: '3–32 letters, numbers or underscores. This cannot be changed later.',
    nickname: 'Nickname',
    nicknameHint: 'Nicknames may repeat. Your Player ID is unique and issued by the system.',
    email: 'Email',
    password: 'Password',
    passwordHint: 'At least 8 characters, with both letters and numbers.',
    confirmPassword: 'Confirm password',
    country: 'Country / region',
    countryPlaceholder: 'Select a country or region',
    forgotPassword: 'Forgot your password?',
    noAccount: 'No account yet?',
    haveAccount: 'Already have an account?',
    registrationClosed: 'Registration is closed',
    registrationClosedBody: 'An administrator has disabled new sign-ups. Please try again later.',
    agreeTerms: 'Registering means you agree to the <termsBtn>Terms</termsBtn>',
    agreement: 'By signing up you agree to follow the community rules.',
    acceptTerms: 'I have read and agree to the <rulesBtn>community rules</rulesBtn>',
    demoHint: 'Demo account: {{identifier}} / {{password}}',
    forgotTitle: 'Recover password',
    forgotSubtitle: 'Enter the email on the account. We will send a 6-digit code so you can set a new password.',
    forgotEmpty: 'The developer has not published contact details yet. Try again later, or use the in-game announcement.',
    contactCopied: 'Contact copied',
    contactCopyFailed: 'Could not copy. Select the text instead.',
    copyContact: 'Copy',
    remember: 'Remember me',
    passwordMismatch: 'The two passwords do not match',
    verifyTitle: 'Verify your email',
    verifySubtitle: 'We sent a 6-digit code to {{email}}. Verify it before you can sign in.',
    verifyCode: 'Verification code',
    verifySubmit: 'Verify and continue',
    sendCode: 'Send code',
    resendCode: 'Resend',
    codeSent: 'Verification code sent. Please check your inbox. If you don’t see it, check your spam folder.',
    forgotIfRegistered: 'If that email is registered, we sent a code.',
    haveCode: 'Already have a code?',
    emailNotVerified: 'Verify your email before signing in.',
    invalidCredentials: 'Incorrect account or password.',
    mailUnavailable: 'Mail is not available right now. Try again later or contact an administrator.',
    resetSubmit: 'Set new password',
    resetDone: 'Password updated. Sign in with the new password.',
    verified: 'Email verified. Please sign in.',
    cannotVerify: "Can't verify?",
    cannotVerifyTitle: "Can't verify email",
    cannotVerifySubtitle:
      'If you cannot receive a code (for example you registered with the wrong mailbox), contact the site owner using the details below. Do not keep sending codes to the wrong address.',
    cannotVerifyEmpty: 'The site owner has not published contact details yet. Try again later, or use the in-game announcement.',
  },
  profile: {
    joined: 'Joined {{date}}',
    lastActive: 'Active {{time}}',
    followers: 'Followers',
    following: 'Following',
    achievements: 'Achievements',
    speedruns: 'Runs',
    points: 'Points',
    follow: 'Follow',
    unfollow: 'Unfollow',
    following_state: 'Following',
    editProfile: 'Edit profile',
    playerId: 'Player ID',
    noBio: 'This player has not written a bio yet.',
    tabOverview: 'Overview',
    tabAchievements: 'Achievements',
    tabSpeedruns: 'Speedruns',
    tabPosts: 'Posts',
    recentAchievements: 'Recently unlocked',
    bestRuns: 'Personal bests',
    pointsBreakdown: 'Where the points came from',
    editTitle: 'Edit your profile',
    editSubtitle: 'This information appears on your public profile.',
    bio: 'Bio',
    bioPlaceholder: 'Tell people who you are, or which record you are chasing.',
    bioCounter: '{{count}} / 280',
    avatar: 'Avatar',
    avatarChange: 'Change avatar',
    avatarRemove: 'Use default',
    avatarHint: 'PNG or JPG, 512×512 recommended, up to 2 MB.',
    cropTitle: 'Crop avatar',
    cropHint: 'Drag the square to choose the area. Nothing is saved until you confirm.',
    avatarUploadClosed: 'Avatar upload is currently disabled.',
    profileUpdated: 'Profile updated',
    groups: 'Roles',
    noAchievementsYet: 'No achievements unlocked yet.',
    noRunsYet: 'No approved speedruns yet.',
    noPostsYet: 'No posts yet.',
    followersTitle: 'People following {{name}}',
    followingTitle: 'People {{name}} follows',
    notFound: 'Player not found',
    notFoundBody: 'This Player ID does not exist, or the account was removed.',
  },
  achievements: {
    title: 'Achievements',
    subtitle: '{{unlocked}} of {{total}} unlocked.',
    publicSubtitle: '{{total}} achievements in total. Sign in to track your own progress.',
    progress: 'Completion',
    unlocked: 'Unlocked',
    locked: 'Locked',
    hidden: 'Hidden achievement',
    hiddenBody: 'Revealed once you unlock it.',
    special: 'Special',
    normal: 'Normal',
    unlockedAt: 'Unlocked {{date}}',
    unlockRate: '{{rate}}% of players have this',
    filterAll: 'All',
    filterUnlocked: 'Unlocked',
    filterLocked: 'Locked',
    filterSpecial: 'Special',
    filterHidden: 'Hidden',
    category: 'Category',
    categories: {
      story: 'Story',
      collection: 'Collection',
      challenge: 'Challenge',
      exploration: 'Exploration',
      speedrun: 'Speedrun',
      community: 'Community',
      account: 'Account',
      secret: 'Secret',
    },
    empty: 'No achievements match these filters.',
    pointsFromAchievements: 'Achievement points',
    detailTitle: 'Achievement',
    unlockCount: 'Earned by {{count}} players',
    recentUnlockers: 'Recently unlocked by',
  },
  speedruns: {
    title: 'My speedruns',
    subtitle: 'Submit runs, follow their review status, and see the points they earned.',
    submitTitle: 'Submit a speedrun',
    submitSubtitle:
      'No video, replay or screenshot is required and nothing is stored. Every run is reviewed by a human.',
    platform: 'Platform',
    version: 'Game version',
    game: 'Game',
    category: 'Category',
    rule: 'Ruleset',
    detailTitle: 'Speedrun',
    duration: 'Duration',
    durationHint: 'Enter as h:mm:ss.mmm, for example 00:29:12.450.',
    note: 'Note',
    notePlaceholder: 'Route, mistakes, anything the reviewer should know (optional).',
    submitAction: 'Submit for review',
    submitClosed: 'Speedrun submission is closed',
    submitClosedBody: 'An administrator has paused submissions. Please try again later.',
    submitted: 'Run submitted and queued for review',
    tierReached: 'Tier reached',
    tierNone: 'No tier reached',
    tierPreview: 'At this time the run qualifies for {{tier}} ({{points}} points).',
    tierPreviewNone: 'This time does not reach a points tier, but the run will still be recorded.',
    status: {
      pending: 'In review',
      approved: 'Approved',
      rejected: 'Rejected',
      revoked: 'Revoked',
      withdrawn: 'Withdrawn',
    },
    statusHint: {
      pending: 'A moderator will review it shortly and you will get a notification.',
      approved: 'This run is on the official leaderboard.',
      rejected: 'This submission did not pass review.',
      revoked: 'This run was revoked after re-review. The points were reversed and the history kept.',
      withdrawn: 'You withdrew this submission.',
    },
    filterStatus: 'Status',
    submittedAt: 'Submitted',
    reviewedAt: 'Reviewed',
    reviewNote: 'Reviewer note',
    playerNote: 'Your note',
    awardedPoints: 'Points awarded',
    rules: 'Ruleset details',
    tiers: 'Points tiers',
    tierUnder: 'Under {{time}}',
    empty: 'You have not submitted a run yet.',
    emptyCta: 'Submit your first run',
    rulesTitle: 'Categories and tiers',
    onlyApproved: 'The official board only lists approved runs.',
    playedAt: 'Play date',
    proofUrl: 'Proof link',
    proofHint: 'Optional. http(s) links only. The server does not store video.',
    withdraw: 'Withdraw submission',
    withdrawnOk: 'Submission withdrawn.',
    emailNotVerified: 'Verify your email before submitting a speedrun.',
  },
  points: {
    title: 'My points',
    subtitle: 'Points come from achievements, speedrun rewards and admin adjustments — all recorded.',
    balance: 'Balance',
    rank: 'Points rank',
    rankOf: 'of {{total}} players',
    earned30: 'Earned in 30 days',
    ledger: 'Ledger',
    ledgerEmpty: 'No points recorded yet.',
    source: 'Source',
    sources: {
      achievement: 'Achievement',
      speedrun: 'Speedrun',
      admin_adjust: 'Admin',
      reversal: 'Reversal',
      system: 'System',
      other: 'Other',
    },
    totalDeducted: 'Total deducted',
    totalEarnedHint: '{{total}} earned in total',
    reason: 'Reason',
    balanceAfter: 'Balance after',
    date: 'Date',
    change: 'Change',
    breakdown: 'Breakdown',
    historyNote:
      'Awarded points never change when a future reward rule changes. When a run is revoked the points are reversed, and the ledger entry is kept.',
  },
  leaderboard: {
    title: 'Leaderboards',
    subtitle: 'Points and speedruns. Only public player information is shown.',
    tabPoints: 'Points',
    tabSpeedruns: 'Speedruns',
    pointsTitle: 'Total points leaderboard',
    speedrunsTitle: 'Speedrun leaderboard',
    rank: '#',
    player: 'Player',
    points: 'Points',
    achievements: 'Achievements',
    trend: 'Trend',
    time: 'Time',
    platform: 'Platform',
    version: 'Version',
    category: 'Category',
    pickCategory: 'Choose a speedrun category to load the board.',
    rule: 'Ruleset',
    tier: 'Tier',
    achievedAt: 'Achieved',
    myRank: 'My rank',
    myRankNone: 'You are not on this board yet.',
    myRankHint: 'Sign in to see your own rank.',
    filters: 'Filters',
    allPlatforms: 'All platforms',
    allVersions: 'All versions',
    allRules: 'All rulesets',
    approvedOnly: 'Approved runs only',
    empty: 'No records match these filters.',
    perPlayerNote: 'Only each player’s best run is listed.',
  },
  community: {
    title: 'Community',
    subtitle: 'Share routes, discuss discoveries, find the people who play like you.',
    newPost: 'New post',
    newPostTitle: 'Write a post',
    postTitle: 'Title',
    postTitlePlaceholder: 'One line that says what you are sharing',
    postBody: 'Body',
    postBodyPlaceholder: 'Line breaks are kept. No off-topic posts or advertising, please.',
    tags: 'Tags',
    board: 'Board',
    allBoards: 'All boards',
    tagsHint: 'Up to 5, press Enter after each.',
    publish: 'Publish',
    published: 'Post published',
    sortRecent: 'Recent',
    sortPopular: 'Most liked',
    sortDiscussed: 'Hot',
    pinned: 'Pinned',
    comments: 'Comments',
    commentCount: '{{count}} comments',
    likeCount: '{{count}} likes',
    addComment: 'Add a comment',
    commentPlaceholder: 'Be kind, be useful.',
    comment: 'Comment',
    commentPublished: 'Comment posted',
    reply: 'Reply',
    noComments: 'No comments yet — say the first thing.',
    like: 'Like',
    liked: 'Liked',
    report: 'Report',
    reportTitle: 'Report content',
    reportSubtitle: 'A moderator will review your report. Please do not submit it twice.',
    reportReason: 'Reason',
    reportDetail: 'Details',
    reportDetailPlaceholder: 'Add context if it helps (optional).',
    reportSubmitted: 'Report submitted. Thank you.',
    reasons: {
      spam: 'Spam or advertising',
      abuse: 'Harassment or abuse',
      cheating: 'Cheating or faked runs',
      nsfw: 'Inappropriate content',
      off_topic: 'Off-topic',
      other: 'Something else',
    },
    closed: 'The community is closed',
    closedBody: 'An administrator has disabled the community for now. Please try again later.',
    signInToPost: 'Sign in to post, comment and like.',
    empty: 'No posts yet.',
    deletePostConfirm: 'Delete this post? This cannot be undone.',
    postDeleted: 'Post deleted',
    searchPlaceholder: 'Search posts',
    byAuthor: 'by',
    readMore: 'Read more',
    notFound: 'Post not found',
    notFoundBody: 'It may have been deleted, or the link is wrong.',
  },
  notifications: {
    title: 'Notifications',
    subtitle: 'Achievements, run reviews, points and community activity.',
    markAllRead: 'Mark all as read',
    markRead: 'Mark as read',
    allRead: 'All notifications marked as read',
    unread: 'Unread',
    filterAll: 'All',
    filterUnread: 'Unread',
    empty: 'No notifications.',
    emptyUnread: 'No unread notifications.',
    unreadBadge: '{{count}} unread',
    kinds: {
      achievement: 'Achievement',
      speedrun: 'Speedrun',
      points: 'Points',
      account: 'Account',
      community: 'Community',
      follow: 'Follow',
      admin: 'Admin',
    },
    types: {
      achievement_unlocked: 'Achievement',
      achievement_revoked: 'Achievement',
      speedrun_approved: 'Speedrun',
      speedrun_rejected: 'Speedrun',
      speedrun_revoked: 'Speedrun',
      points_adjusted: 'Points',
      account_banned: 'Account',
      account_unbanned: 'Account',
      account_updated: 'Account',
      post_commented: 'Community',
      post_liked: 'Community',
      comment_replied: 'Community',
      new_follower: 'Follow',
      report_resolved: 'Community',
      admin_message: 'Admin',
      system: 'System',
    },
  },
  settings: {
    title: 'Account settings',
    subtitle: 'Manage your profile, preferences and security.',
    tabProfile: 'Profile',
    tabPreferences: 'Preferences',
    tabSecurity: 'Security',
    preferencesTitle: 'Interface preferences',
    preferencesSubtitle: 'Language is saved to your account. Theme stays on this device.',
    securityTitle: 'Password & sign-in',
    changePassword: 'Change password',
    currentPassword: 'Current password',
    newPassword: 'New password',
    passwordChanged: 'Password changed',
    sessions: 'Signed-in devices',
    sessionsSubtitle: 'Includes sessions created by the game client.',
    currentSession: 'This device',
    revokeSession: 'Revoke',
    sessionRevoked: 'Device signed out',
    lastUsed: 'Last used {{time}}',
    accountTitle: 'Account',
    email: 'Email',
    emailNone: 'Not bound',
    emailVerified: 'Verified',
    emailUnverified: 'Unverified',
    emailUnbound: 'Not bound',
    bindEmail: 'Bind email',
    bindEmailHint: 'This account has no email yet. Send a code to the new address, then submit it.',
    bindEmailDone: 'Email bound.',
    verifyCurrentEmail: 'Verify current email',
    verifyCurrentEmailHint: 'This address is not verified yet. Send a code to it, then submit.',
    verifyEmailDone: 'Email verified.',
    bindEmailCodeSent: 'Verification code sent. Please check your inbox. If you don’t see it, check your spam folder.',
    verifyEmailCodeSent: 'Verification code sent. Please check your inbox. If you don’t see it, check your spam folder.',
    playerIdHint: 'Your Player ID is your permanent identifier and cannot be changed.',
    dangerTitle: 'Danger zone',
    dangerBody:
      'Deleting your account soft-deletes your profile. Achievements, runs, points and audit records are kept.',
    dangerConfirm: 'Type DELETE to confirm',
    deleteAccount: 'Delete account',
    deleteAccountDone: 'Account deleted',
    changeEmail: 'Change email',
    newEmail: 'New email',
    emailChanged: 'Email updated.',
    emailCode: 'Email code',
    sendEmailCode: 'Send code',
    emailCodeSent: 'Verification code sent. Please check your inbox. If you don’t see it, check your spam folder.',
    confirmPassword: 'Confirm new password',
    revokeOthers: 'Sign out other sessions',
    othersRevoked: 'Other sessions signed out',
    gameTokens: 'Game client logins',
    gameTokensSubtitle: 'Revoking a token means you will need to sign in again in the game.',
    revokeToken: 'Revoke',
    tokenRevoked: 'Game login revoked',
    device: 'Bound device',
    deviceNone: 'No game device is bound.',
    deviceBound: 'Bound',
    username: 'Username',
  },
  errors: {
    title: 'Something went wrong',
    generic: 'The request failed. Please try again.',
    network: 'Network request failed. Check your connection and retry.',
    unauthorized: 'Please sign in first.',
    forbidden: 'You do not have permission to do that.',
    notFound: 'Page not found',
    notFoundBody: 'The link may have expired, or the page moved.',
    validation: 'Please check the highlighted fields.',
    rateLimited: 'Too many attempts. Try again in {{seconds}}s.',
    rateLimitedShort: 'Too many attempts. Please try again shortly.',
    banned: 'Account suspended',
    bannedBody: 'Your account is currently suspended and cannot perform this action.',
    bannedReason: 'Reason: {{reason}}',
    bannedUntil: 'Until: {{date}}',
    bannedPermanent: 'This suspension is permanent.',
    maintenance: 'Under maintenance',
    maintenanceBody: 'We are performing maintenance. Please check back shortly.',
    featureDisabled: 'This feature is currently disabled.',
    goHome: 'Back to home',
    fields: {
      required: 'This field is required',
      invalid: 'This is not a valid value',
      taken: 'This is already taken',
      too_long: 'This is too long',
      too_short: 'This is too short',
      incorrect: 'This is not correct',
      implausible: 'That duration does not look plausible',
      blocked_word: 'Contains content that is not allowed',
      invalid_type: 'That file type is not supported',
    },
  },
  ban: {
    banner: 'Your account is suspended. You can browse, but not post.',
  },
};
